import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { DIAS_CURTOS, DIAS_LONGOS } from "@/components/Agenda/agenda-utils";
import BlockDialog, { type BloqueioEditavel } from "@/components/Agenda/BlockDialog";
import GoogleCalendarCard from "@/components/Agenda/GoogleCalendarCard";
import GoogleCalendarIcon from "@/components/Agenda/GoogleCalendarIcon";
import {
  Building2,
  CalendarOff,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Pencil,
  Plus,
  Save,
  Settings,
  X,
} from "lucide-react";

type Faixa = { dayOfWeek: number; startTime: string; endTime: string };
type Filtro = "todos" | "meus" | "google";

const PADRAO_COMERCIAL: Faixa[] = [1, 2, 3, 4, 5].flatMap((d) => [
  { dayOfWeek: d, startTime: "09:00", endTime: "12:00" },
  { dayOfWeek: d, startTime: "14:00", endTime: "18:00" },
]);

const POR_PAGINA = 8;

/**
 * Disponibilidade da pessoa: os bloqueios em primeiro plano.
 *
 * O que a equipe mexe toda semana é bloqueio — férias, médico, folga. Isso
 * ocupa a tela inteira, em lista paginada e filtrável. A semana típica e a
 * conexão com o Google são cadastro: fazem-se uma vez e vão para o diálogo de
 * configurações, na engrenagem. Antes dividiam espaço em três cartões e a
 * lista de bloqueios cabia em meia dúzia de linhas.
 */
export default function AvailabilityPanel({
  userId,
  configAberta = false,
  onFecharConfig,
}: {
  /** Pessoa em foco — vem do seletor único da barra de cima. */
  userId?: number;
  /** As configurações abrem pela engrenagem, que mora ao lado das abas. */
  configAberta?: boolean;
  onFecharConfig?: () => void;
}) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const meuId = (user as any)?.id as number | undefined;

  const podeGerenciarOutros = can("agenda:availability_manage_all");
  // Sem escolha na barra ("Toda a equipe"), a disponibilidade mostrada é a
  // própria: bloqueio e semana típica são sempre de UMA pessoa.
  const alvoId = userId ?? meuId;
  const editandoOutro = alvoId != null && alvoId !== meuId;
  const somenteLeitura = editandoOutro && !podeGerenciarOutros;

  return (
    <div className="space-y-4">
      <BlocksCard userId={alvoId} somenteLeitura={somenteLeitura} mostrarPessoa={podeGerenciarOutros} />

      <p className="text-xs text-muted-foreground">
        Horários de reunião e a conexão com o Google Agenda ficam na engrenagem, ao lado das abas.
      </p>

      <ConfigDialog
        open={configAberta}
        onClose={() => onFecharConfig?.()}
        alvoId={alvoId}
        somenteLeitura={somenteLeitura}
        /* A conexão do Google é da PESSOA logada: só aparece na própria agenda. */
        mostrarGoogle={!editandoOutro}
      />
    </div>
  );
}

// ─── Configurações da agenda ─────────────────────────────────────────────────

function ConfigDialog({
  open,
  onClose,
  alvoId,
  somenteLeitura,
  mostrarGoogle,
}: {
  open: boolean;
  onClose: () => void;
  alvoId?: number;
  somenteLeitura: boolean;
  mostrarGoogle: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto bg-white dark:bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Configurações da agenda
          </DialogTitle>
          <DialogDescription>
            Horários em que você aceita reunião e a conexão com o Google Agenda.
          </DialogDescription>
        </DialogHeader>

        {/* Duas coisas independentes: quando você atende, e de onde vêm os
            compromissos. Em abas, cada uma cabe inteira na tela. */}
        <Tabs defaultValue="horarios">
          <TabsList className="self-start">
            <TabsTrigger value="horarios">
              <Clock className="h-3.5 w-3.5 mr-1.5" />
              Horários
            </TabsTrigger>
            {mostrarGoogle && (
              <TabsTrigger value="google">
                <GoogleCalendarIcon className="h-3.5 w-3.5 mr-1.5" />
                Google Agenda
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="horarios" className="mt-3">
            <TypicalWeekCard alvoId={alvoId} somenteLeitura={somenteLeitura} />
          </TabsContent>
          {mostrarGoogle && (
            <TabsContent value="google" className="mt-3">
              <GoogleCalendarCard />
            </TabsContent>
          )}
        </Tabs>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Semana típica ───────────────────────────────────────────────────────────

function TypicalWeekCard({ alvoId, somenteLeitura }: { alvoId?: number; somenteLeitura: boolean }) {
  const utils = trpc.useUtils();
  const availabilityQuery = trpc.agenda.availability.list.useQuery(
    { userId: alvoId },
    { enabled: alvoId != null },
  );

  const [faixas, setFaixas] = useState<Faixa[]>([]);
  const [sujo, setSujo] = useState(false);
  // De quem é a semana que está no formulário agora. Salvar é replace-all, e
  // salvar a semana de A dentro do registro de B apaga a de B — então o
  // formulário só pode ser enviado quando isto bate com o alvo selecionado.
  const [carregadoDe, setCarregadoDe] = useState<number | null>(null);

  useEffect(() => {
    // Trocar de pessoa esvazia na hora: sem isso, a semana da pessoa anterior
    // fica na tela enquanto a nova carrega, e um clique rápido em Salvar a
    // grava por cima da certa.
    setFaixas([]);
    setSujo(false);
    setCarregadoDe(null);
  }, [alvoId]);

  useEffect(() => {
    if (!availabilityQuery.data || alvoId == null) return;
    setFaixas(
      availabilityQuery.data.map((r: any) => ({
        dayOfWeek: r.dayOfWeek,
        startTime: r.startTime,
        endTime: r.endTime,
      })),
    );
    setSujo(false);
    setCarregadoDe(alvoId);
  }, [availabilityQuery.data, alvoId]);

  const salvar = trpc.agenda.availability.set.useMutation({
    onSuccess: () => {
      toast.success("Disponibilidade salva");
      setSujo(false);
      utils.agenda.availability.list.invalidate();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao salvar disponibilidade")),
  });

  function atualizar(idx: number, patch: Partial<Faixa>) {
    setFaixas((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
    setSujo(true);
  }

  function adicionar(dia: number) {
    // Nova faixa começa onde a última do dia terminou — segunda faixa do dia
    // quase sempre é "a tarde".
    const ultima = faixas
      .filter((f) => f.dayOfWeek === dia)
      .sort((a, b) => (a.endTime < b.endTime ? -1 : 1))
      .at(-1);
    const nova: Faixa = ultima
      ? { dayOfWeek: dia, startTime: somaHoras(ultima.endTime, 2), endTime: somaHoras(ultima.endTime, 6) }
      : { dayOfWeek: dia, startTime: "09:00", endTime: "18:00" };
    setFaixas((prev) => [...prev, nova]);
    setSujo(true);
  }

  function remover(idx: number) {
    setFaixas((prev) => prev.filter((_, i) => i !== idx));
    setSujo(true);
  }

  function validarEsalvar() {
    // Trava final contra gravar a semana de uma pessoa no registro de outra.
    if (alvoId == null || carregadoDe !== alvoId) {
      toast.error("Aguarde a agenda desta pessoa terminar de carregar.");
      return;
    }
    for (const f of faixas) {
      if (f.endTime <= f.startTime) {
        toast.error(`${DIAS_LONGOS[f.dayOfWeek]}: ${f.startTime}–${f.endTime} não é uma faixa válida.`);
        return;
      }
    }
    salvar.mutate({ userId: alvoId, slots: faixas });
  }

  return (
    <Card className="bg-white dark:bg-card">
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Horários de reunião
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            semana típica · usada para sugerir horários livres
          </p>
        </div>
        {!somenteLeitura && (
          <div className="flex gap-1.5 shrink-0">
            {faixas.length === 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => {
                  setFaixas(PADRAO_COMERCIAL);
                  setSujo(true);
                }}
              >
                Usar horário comercial
              </Button>
            )}
            <Button
              size="sm"
              className="h-7 text-xs"
              disabled={!sujo || salvar.isPending || availabilityQuery.isLoading || carregadoDe !== alvoId}
              onClick={validarEsalvar}
            >
              <Save className="h-3.5 w-3.5 mr-1" />
              {salvar.isPending ? "Salvando…" : "Salvar"}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-0.5">
        {/* Segunda a domingo — ordem em que a semana de trabalho é lida. */}
        {[1, 2, 3, 4, 5, 6, 0].map((dia) => {
          const doDia = faixas.map((f, idx) => ({ f, idx })).filter(({ f }) => f.dayOfWeek === dia);
          return (
            <div
              key={dia}
              className={`flex items-center gap-2 min-h-9 rounded-md px-1 -mx-1 hover:bg-muted/40 ${
                doDia.length === 0 ? "bg-muted/20" : ""
              }`}
            >
              <span
                className={`w-9 shrink-0 text-xs font-medium ${doDia.length === 0 ? "text-muted-foreground" : ""}`}
              >
                {DIAS_CURTOS[dia]}
              </span>
              <div className="flex flex-1 flex-wrap items-center gap-x-3 gap-y-1">
                {doDia.length === 0 && <span className="text-xs text-muted-foreground/70">Indisponível</span>}
                {doDia.map(({ f, idx }) => (
                  <span key={idx} className="inline-flex items-center gap-1">
                    <Input
                      type="time"
                      className="h-7 w-[5.4rem] px-1.5 text-xs"
                      value={f.startTime}
                      disabled={somenteLeitura}
                      onChange={(e) => atualizar(idx, { startTime: e.target.value })}
                    />
                    <span className="text-muted-foreground text-xs">–</span>
                    <Input
                      type="time"
                      className="h-7 w-[5.4rem] px-1.5 text-xs"
                      value={f.endTime}
                      disabled={somenteLeitura}
                      onChange={(e) => atualizar(idx, { endTime: e.target.value })}
                    />
                    {!somenteLeitura && (
                      <button
                        type="button"
                        className="h-6 w-6 inline-flex items-center justify-center rounded text-muted-foreground hover:text-destructive"
                        aria-label="Remover faixa"
                        onClick={() => remover(idx)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {!somenteLeitura && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 shrink-0 text-xs text-primary"
                  aria-label={`Adicionar faixa em ${DIAS_LONGOS[dia]}`}
                  onClick={() => adicionar(dia)}
                >
                  <Plus className="h-3.5 w-3.5 mr-0.5" />
                  faixa
                </Button>
              )}
            </div>
          );
        })}
        <p className="pt-2 text-[11px] text-muted-foreground">
          {faixas.length === 0
            ? "Sem semana típica, esta pessoa não aparece em nenhuma sugestão de horário livre."
            : "Duas faixas no mesmo dia formam o intervalo do almoço, que não é oferecido para reunião."}
        </p>
      </CardContent>
    </Card>
  );
}

function somaHoras(hhmm: string, h: number): string {
  const [hh, mm] = hhmm.split(":").map(Number);
  const total = Math.min(23 * 60 + 59, hh * 60 + mm + h * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

// ─── Bloqueios ───────────────────────────────────────────────────────────────

function BlocksCard({
  userId,
  somenteLeitura,
  mostrarPessoa,
}: {
  userId?: number;
  somenteLeitura: boolean;
  mostrarPessoa: boolean;
}) {
  const utils = trpc.useUtils();
  const [dialogo, setDialogo] = useState<{ aberto: boolean; bloqueio: BloqueioEditavel | null }>({
    aberto: false,
    bloqueio: null,
  });
  const [filtro, setFiltro] = useState<Filtro>("todos");
  const [pag, setPag] = useState(0);
  const [removendoSerie, setRemovendoSerie] = useState<any | null>(null);

  // Bloqueio antigo não interessa: a lista é da agenda daqui para a frente.
  const desde = new Date();
  desde.setHours(0, 0, 0, 0);

  const blocksQuery = trpc.agenda.blocks.list.useQuery(
    { userId, from: desde },
    { enabled: userId != null },
  );

  const remover = trpc.agenda.blocks.delete.useMutation({
    onSuccess: () => {
      toast.success("Bloqueio removido");
      utils.agenda.blocks.list.invalidate();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao remover o bloqueio")),
  });

  const todos = (blocksQuery.data ?? []) as any[];
  const lista = useMemo(
    () =>
      todos.filter((row) =>
        filtro === "google"
          ? row.block.externalProvider === "google"
          : filtro === "meus"
            ? row.block.externalProvider == null
            : true,
      ),
    [todos, filtro],
  );
  const totalPags = Math.max(1, Math.ceil(lista.length / POR_PAGINA));
  const pagina = Math.min(pag, totalPags - 1);
  const inicio = pagina * POR_PAGINA;
  const visiveis = lista.slice(inicio, inicio + POR_PAGINA);

  // Identidade estável: o diálogo repreenche o formulário quando `defaults`
  // muda, e um objeto novo por render o resetaria no meio da digitação.
  const defaults = useMemo(() => ({ userId }), [userId]);

  const filtros: Array<{ v: Filtro; label: string }> = [
    { v: "todos", label: "Todos" },
    { v: "meus", label: "Meus bloqueios" },
    { v: "google", label: "Do Google" },
  ];

  return (
    <Card>
      <CardHeader className="pb-3 space-y-3">
        <div className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarOff className="h-4 w-4" />
            Bloqueios
            {lista.length > 0 && <Badge variant="secondary">{lista.length}</Badge>}
          </CardTitle>
          {!somenteLeitura && (
            <Button size="sm" className="h-7 text-xs" onClick={() => setDialogo({ aberto: true, bloqueio: null })}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Bloquear
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {filtros.map((f) => (
              <button
                key={f.v}
                type="button"
                onClick={() => {
                  setFiltro(f.v);
                  setPag(0);
                }}
                className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                  filtro === f.v
                    ? "border-primary bg-primary/10 text-primary font-medium"
                    : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {totalPags > 1 && (
            <span className="text-[11px] text-muted-foreground">
              página {pagina + 1} de {totalPags}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {blocksQuery.isLoading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Carregando…</p>
        ) : lista.length === 0 ? (
          <div className="py-10 text-center">
            <CalendarOff className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
            <p className="text-sm text-muted-foreground">
              {filtro === "google"
                ? "Nenhum compromisso do Google daqui para a frente."
                : filtro === "meus"
                  ? "Nenhum bloqueio seu daqui para a frente."
                  : "Nenhum bloqueio daqui para a frente."}
            </p>
            {!somenteLeitura && filtro !== "google" && (
              <p className="text-xs text-muted-foreground/80 mt-1">
                Férias, médico, folga: bloqueie para ninguém marcar reunião nesse horário.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-1.5">
            {visiveis.map((row: any) => {
              const espelho = row.block.sourceMeetingId != null;
              const google = row.block.externalProvider === "google";
              const editavel = !somenteLeitura && !espelho && !google;
              return (
                <div
                  key={row.block.id}
                  className={`group flex items-center gap-3 rounded-lg border p-2 transition-colors ${
                    google
                      ? "border-amber-200 bg-amber-50/60 dark:border-amber-900/60 dark:bg-amber-950/20"
                      : "border-border bg-card"
                  } ${editavel ? "cursor-pointer hover:border-primary/40" : ""}`}
                  onClick={() => editavel && setDialogo({ aberto: true, bloqueio: row.block })}
                  role={editavel ? "button" : undefined}
                  tabIndex={editavel ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (editavel && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      setDialogo({ aberto: true, bloqueio: row.block });
                    }
                  }}
                >
                  {/* Data em bloco: o olho corre a coluna e acha o dia. */}
                  <div className="w-12 shrink-0 text-center">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground leading-none">
                      {mesCurto(row.block.startAt)}
                    </p>
                    <p className="text-lg font-semibold leading-tight">{diaDoMes(row.block.startAt)}</p>
                    <p className="text-[10px] text-muted-foreground leading-none">
                      {dowCurto(row.block.startAt, row.block.endAt, row.block.allDay)}
                    </p>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {row.block.reason || (google ? "Compromisso" : "Sem motivo")}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {periodoLegivel(row.block.startAt, row.block.endAt, row.block.allDay)}
                      {mostrarPessoa && row.user?.name ? ` · ${row.user.name}` : ""}
                      {row.block.seriesId ? " · repete toda semana" : ""}
                    </p>
                  </div>
                  <Badge
                    variant="outline"
                    className={`shrink-0 gap-1 font-normal text-[10px] hidden sm:inline-flex ${
                      google ? "border-amber-300 dark:border-amber-800" : ""
                    }`}
                  >
                    {google ? (
                      <>
                        <GoogleCalendarIcon className="h-3 w-3" /> Google Agenda
                      </>
                    ) : espelho ? (
                      <>
                        <Building2 className="h-3 w-3" /> Outra empresa
                      </>
                    ) : (
                      <>
                        <CalendarOff className="h-3 w-3" /> Bloqueio manual
                      </>
                    )}
                  </Badge>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {google ? (
                      row.block.externalUrl ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-muted-foreground"
                          aria-label="Abrir no Google Agenda"
                          title="Abrir no Google Agenda"
                          asChild
                        >
                          <a
                            href={row.block.externalUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </Button>
                      ) : null
                    ) : editavel ? (
                      <>
                        <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          aria-label="Remover bloqueio"
                          disabled={remover.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Numa repetição, apagar só este dia ou a série
                            // inteira são decisões diferentes: pergunta.
                            if (row.block.seriesId) {
                              setRemovendoSerie(row.block);
                              return;
                            }
                            remover.mutate({ id: row.block.id });
                          }}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>
              );
            })}

            {totalPags > 1 && (
              <div className="flex items-center justify-between gap-2 pt-2 flex-wrap">
                <span className="text-[11px] text-muted-foreground">
                  {inicio + 1}–{Math.min(inicio + POR_PAGINA, lista.length)} de {lista.length} bloqueios
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    disabled={pagina === 0}
                    onClick={() => setPag((p) => Math.max(0, p - 1))}
                  >
                    <ChevronLeft className="h-3.5 w-3.5 mr-0.5" />
                    Anterior
                  </Button>
                  {Array.from({ length: totalPags }, (_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPag(i)}
                      className={`h-7 min-w-7 px-1.5 rounded-md text-xs transition-colors ${
                        i === pagina
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    disabled={pagina >= totalPags - 1}
                    onClick={() => setPag((p) => Math.min(totalPags - 1, p + 1))}
                  >
                    Próxima
                    <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>

      <BlockDialog
        open={dialogo.aberto}
        onClose={() => setDialogo((d) => ({ ...d, aberto: false }))}
        bloqueio={dialogo.bloqueio}
        defaults={defaults}
      />

      <AlertDialog open={!!removendoSerie} onOpenChange={(o) => !o && setRemovendoSerie(null)}>
        <AlertDialogContent className="bg-white dark:bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover a repetição?</AlertDialogTitle>
            <AlertDialogDescription>
              “{removendoSerie?.reason || "Este bloqueio"}” se repete toda semana. Remover a
              repetição apaga as próximas ocorrências; o que já passou fica no histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:justify-between">
            <AlertDialogCancel className="sm:mr-auto">Voltar</AlertDialogCancel>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={remover.isPending}
                onClick={() => {
                  remover.mutate({ id: removendoSerie.id });
                  setRemovendoSerie(null);
                }}
              >
                Só este dia
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={remover.isPending}
                onClick={() => {
                  remover.mutate({ id: removendoSerie.id, serieInteira: true });
                  setRemovendoSerie(null);
                }}
              >
                Toda a repetição
              </Button>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ─── Rótulos de data ─────────────────────────────────────────────────────────

function mesCurto(d: Date | string): string {
  return new Date(d).toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
}
function diaDoMes(d: Date | string): string {
  return String(new Date(d).getDate()).padStart(2, "0");
}

/** "qua" ou "qua → qui" quando o bloqueio atravessa dias. */
function dowCurto(ini: Date | string, fim: Date | string, allDay: boolean): string {
  const a = new Date(ini);
  // Dia inteiro termina à meia-noite seguinte: o último dia coberto é o anterior.
  const b = new Date(allDay ? new Date(fim).getTime() - 1 : new Date(fim).getTime());
  const curto = (d: Date) => d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "");
  return a.toDateString() === b.toDateString() ? curto(a) : `${curto(a)} → ${curto(b)}`;
}

function periodoLegivel(ini: Date | string, fim: Date | string, allDay: boolean): string {
  const a = new Date(ini);
  const b = new Date(fim);
  if (allDay) {
    const ultimo = new Date(b.getTime() - 1);
    const dias = Math.round((ultimo.getTime() - a.getTime()) / 86_400_000) + 1;
    return dias > 1 ? `Dia inteiro · ${dias} dias` : "Dia inteiro";
  }
  const hh = (d: Date) =>
    `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${hh(a)} – ${hh(b)}`;
}
